import { inject } from '@angular/core';
import { SiteMenuSettings } from '@core/models/site-menu-settings.model';
import { EnvironmentService } from '@core/services/environment/environment.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { SiteMenuSettingsService } from '@core/services/site-menu-settings/site-menu-settings.service';
import { MenuItem } from 'primeng/api';

export abstract class BasePageLayoutComponent {
  protected readonly environmentService = inject(EnvironmentService);
  protected readonly siteMenuSettingsService = inject(SiteMenuSettingsService);
  protected readonly logger: ReturnType<LoggerService['forContext']>;

  items: MenuItem[] = [];
  menuSettings: SiteMenuSettings | null = null;

  constructor(loggerContext: string) {
    this.logger = inject(LoggerService).forContext(loggerContext);
  }

  protected loadMenuSettings(clientId: number): void {
    this.siteMenuSettingsService.getCachedSettings(clientId).subscribe({
      next: (settings) => {
        this.menuSettings = settings;
        this.setMenu();
      },
      error: (error) => {
        this.logger.error('Error loading menu settings:', error);
        this.setMenu();
      },
    });
  }

  protected subscribeToSettingsChanges(clientId: number): void {
    this.siteMenuSettingsService.getSettingsUpdates().subscribe({
      next: (update) => {
        if (update && update.clientId === clientId) {
          this.menuSettings = update.settings;
          this.setMenu();
        }
      },
    });
  }

  setMenu(): void {
    const items: MenuItem[] = [];

    if (this.menuSettings?.whatsapp_number) {
      items.push({
        label: 'Hablemos por WhatsApp',
        icon: 'pi pi-whatsapp',
        command: () => {
          if (this.validateBrowser()) {
            const clean = this.menuSettings!.whatsapp_number!.replace(/\D/g, '');
            window.open(`https://wa.me/${clean}`, '_blank');
          }
        },
      });
    }

    if (this.menuSettings?.customer_service_phone) {
      items.push({
        label: 'Llámanos a nuestra linea',
        icon: 'pi pi-phone',
        command: () => {
          if (this.validateBrowser()) {
            window.open(`tel:${this.menuSettings!.customer_service_phone}`, '_blank');
          }
        },
      });
    }

    if (this.menuSettings?.customer_service_email) {
      items.push({
        label: 'Contáctenos por Email',
        icon: 'pi pi-at',
        command: () => {
          if (this.validateBrowser()) {
            window.open(`mailto:${this.menuSettings!.customer_service_email}`, '_blank');
          }
        },
      });
    }

    if (this.menuSettings?.chatbot_url) {
      items.push({
        label: 'Chat en linea',
        icon: 'pi pi-comments',
        command: () => {
          if (this.validateBrowser()) {
            window.open(this.menuSettings!.chatbot_url!, '_blank');
          }
        },
      });
    }

    if (this.menuSettings?.technical_service_url) {
      items.push({
        label: 'Soporte técnico',
        icon: 'pi pi-cog',
        command: () => {
          if (this.validateBrowser()) {
            window.open(this.menuSettings!.technical_service_url!, '_blank');
          }
        },
      });
    }

    if (this.menuSettings?.map_url) {
      items.push({
        label: 'Abrir en Google Maps',
        icon: 'pi pi-map-marker',
        command: () => {
          if (this.validateBrowser()) {
            window.open(this.menuSettings!.map_url!, '_blank');
          }
        },
      });
    }

    this.items = items;
  }

  validateBrowser(): boolean {
    return this.environmentService.isBrowser();
  }
}
